<script setup lang="ts">
import type { ExclusifyUnion } from 'type-fest'
import { computed, ref, watch, type Directive, useId } from 'vue'
import { isEqual, uniqBy } from 'lodash-es'
import { match, P } from 'ts-pattern'
import { vOnClickOutside } from '@vueuse/components'
import { useClipboard } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { fw } from './attributes'
import { useContext } from './context'

import Button from '~/ui/Button.vue'
import Label from '~/ui/Label.vue'

// #region props
const {
  suggestions = [],
  ...props
} = defineProps<{
  suggestions?: Token[]
  suggestionsOnly?: boolean
  categoriesUnique?: boolean
  prefix?: '#'
  required?: boolean
  label?: string
}>()
// #endregion props

const context = useContext()

// #region model
const model
  = defineModel<Token[]>({
    required: true,
    default: () => []
  })
// #endregion model

// #region token
type Token
  = Readonly<{
    label: string
    categoryId?: string
    isCustom?: boolean
  }>
// #endregion token

const { t } = useI18n()

const anchorName = `--${useId()}`;

/**
 * Side effect. Naming: Verb(-Noun)
 */
type Effect
  = | 'sync-model'
    | { type: 'write-clipboard', tokens: Token[] }

/**
 * Editor state with draft of tokens. Naming: Gerund (an ongoing user activity)
 */
type State = Readonly<{
  tokens: Token[]
  undo?: State
  redo?: State
} & ExclusifyUnion<(
  | {
    readonly type: 'idle'
  }
  | {
    readonly type: 'editing-gap' | 'editing-pill'
    /** Cursor index (0..tokens.length) */
    readonly cursor: number
    readonly suggestion?: string
    readonly query: string
  }
  | {
    readonly type: 'selecting-pills'
    /**
     * Cursor index (0..tokens.length)
     * Next to most recently selected pill
     */
    readonly cursor: number
    /**
     * Other end of continuous selection(0..tokens.length)
     */
    readonly origin: number
  }
)>>

/**
 * Registered user input. Naming: Past participle, verb-first
 */
type Event = Readonly<
  // External Synchronization
  | { type: 'updated-tokens', tokens: Token[] }

  // Focus Management
  | { type: 'lost-focus' }

  // Direct Interaction
  | { type: 'activated', target: 'gap' | 'pill', index: number, extendSelection: boolean }

  // Logical Navigation
  | { type: 'navigated', target: `${'inline' | 'block'}-${'prev' | 'next'}` }

  // Selection
  | { type: 'extended-selection', target: `inline-${'prev' | 'next'}` }
  | { type: 'selected-all' }

  // Draft Edit
  | { type: 'typed-query', query: string }

  // Removal
  | { type: 'removed', target: 'backward' | 'forward' | 'selection' }

  // Lifecycle
  | { type: 'committed' }
  | { type: 'canceled' }
  | { type: 'undone' }
  | { type: 'redone' }

  // Clipboard
  | { type: 'pasted', tokens: Token[] }
  | { type: 'copied' }
  | { type: 'cut' }

  // Suggestions
  // Note: Pressing mouse down marks suggestion; full click then commits (user can pull mouse away)
  | { type: 'marked-suggestion', suggestion: string }
>

const state = ref<State>({ tokens: model.value, type: 'idle'})

//  Side effect: Keep tokens...
// - in sync with model
// - unique
watch(model, tokens => dispatch({
  type: 'updated-tokens',
  tokens: uniqBy(tokens, t => t.label.toLowerCase()
  ) }))

// Active query for suggestions
const currentQuery = computed(() =>
  match(state.value)
    .with({ type: 'editing-gap' }, s => s.query)
    .with({ type: 'editing-pill' }, s => s.query)
    .otherwise(() => '')
)

/**
 * Suggestions filtered by:
 * - is unused
 * - has fresh category (if categoriesUnique prop is set)
 * and ordered by string proximity (simple replacement algorithm)
 */
const activeSuggestions = computed(() => {
  if (state.value.type === 'idle' || state.value.type === 'selecting-pills') return []
  if (state.value.type === 'editing-gap' && !currentQuery.value) return []

  const usedLabels = new Set(state.value.tokens.map(t => t.label))
  const forbiddenCategories = new Set(state.value.tokens.map((t, index) => 'cursor' in state.value && index !== state.value.cursor! && t.categoryId).filter(Boolean))

  const isUnused = (t: Token) => !usedLabels.has(t.label)
  const hasFreshCategory = (t: Token) =>
    !props.categoriesUnique
    || !t.categoryId
    || !('cursor' in state.value)
    || !forbiddenCategories.has(t.categoryId)

  return search<Token>(
    currentQuery.value,
    suggestions,
    token => token.label,
    {
      limit: 12,
      predicate: t => isUnused(t) && hasFreshCategory(t)
    }
  )
})

// DEBUG
const dispatchHistory = ref<Array<unknown>>([])

// Clipboard
const { copy } = useClipboard()

// ====================================================================
// Update
// ====================================================================

/**
 * Update state
 * - Idempotent: "Applying the same input multiple times results in the same state as applying it once"
 * - use 'commit' instead to commit edits first
 * @param event
 */
const dispatch = (event: Event) => {
  const [newState, effects] = match<Event, [State, Effect[]]>(event)

    .with({ type: 'updated-tokens' }, e => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'idle' }, s => [{ ...s, tokens: e.tokens }, []])
      .with({ type: 'editing-pill' }, ({ query, ...s }) => [{
        ...s,
        tokens: e.tokens,
        // Clamp cursor
        cursor: Math.min(s.cursor, e.tokens.length),
        // Reset query if currently editing token has changed remotely
        query: e.tokens[s.cursor] && s.tokens[s.cursor] !== e.tokens[s.cursor]
          ? e.tokens[s.cursor]!.label
          : query
      }, []])
      .with({ type: 'editing-gap' }, s => [{
        ...s,
        tokens: e.tokens,
        // Clamp cursor
        cursor: Math.min(s.cursor, e.tokens.length)
      }, []])
      .with({ type: 'selecting-pills' }, s => [{
        ...s,
        tokens: e.tokens,
        // Clamp cursor and origin
        cursor: Math.min(s.cursor, e.tokens.length),
        origin: Math.min(s.origin, e.tokens.length)
      }, []])
      .exhaustive()
    )

    .with({ type: 'lost-focus' }, _ => match<State, [State, Effect[]]>(state.value)
      .with(P.any, ({ origin: _, cursor: __, query: ___, suggestion: ____, ...s }) => [{
        ...s,
        type: 'idle'
      }, []])
      .exhaustive()
    )

    .with({ type: 'activated', extendSelection: false }, e => match<State, [State, Effect[]]>(state.value)
      .with(P.any, ({ tokens, origin: _, ...s }) => [{
        ...s,
        tokens,
        type: `editing-${e.target}` as const,
        cursor: e.index,
        query: e.target === 'pill' ? tokens[e.index]!.label : ''
      }, []])
      .exhaustive()
    )

    .with({ type: 'activated', extendSelection: true }, e => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'selecting-pills' }, ({ cursor, origin, ...s }) => [{
        ...s,
        cursor: e.index >= origin && e.target === 'pill'
          // Surround the activated pill at `index`
          ? e.index + 1
          : e.index,
        origin: Math.abs(origin - cursor) === 1
          ? (e.index < Math.max(origin, cursor) ? Math.max(origin, cursor) : Math.min(origin, cursor))
          : origin
      }, []])
      .with({ type: 'editing-pill' }, ({ undo, redo, tokens, cursor, origin }) => [{
        undo, redo, tokens,
        type: 'selecting-pills',
        origin: cursor,
        cursor: e.index >= cursor && e.target !== 'gap' ? e.index + 1 : e.index
      }, []])
      .otherwise(s => [s, []])
    )

    .with({ type: 'navigated', target: 'inline-prev' }, e => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'idle' }, () => [state.value, []])
      .with({ type: 'editing-gap', cursor: 0 }, () => [state.value, []])
      .with({ type: 'editing-gap' }, ({ tokens, cursor, query, ...s }) => [{
        ...s,
        tokens,
        type: 'editing-pill',
        cursor: cursor - 1,
        query: tokens[cursor - 1]!.label
      }, []])
      .with({ type: 'editing-pill' }, s => [{
        ...s,
        type: 'editing-gap',
        query: ''
      }, []])
      .with({ type: 'selecting-pills' }, ({ cursor, origin, ...s }) => [{
        ...s,
        type: 'editing-gap',
        cursor: Math.max(0, cursor < origin ? cursor : cursor - 1),
        query: ''
      }, []])
      .exhaustive()
    )

    .with({ type: 'navigated', target: 'inline-next' }, e => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'idle' }, () => [state.value, []])
      .with({ type: 'editing-gap', cursor: state.value.tokens.length }, () => [state.value, []])
      .with({ type: 'editing-gap' }, ({ tokens, cursor, query, ...s }) => [{
        ...s,
        tokens,
        type: 'editing-pill',
        cursor,
        query: tokens[cursor]!.label
      }, []])
      .with({ type: 'editing-pill' }, ({ cursor, ...s }) => [{
        ...s,
        type: 'editing-gap',
        cursor: cursor + 1,
        query: ''
      }, []])
      .with({ type: 'selecting-pills' }, ({ cursor, origin, ...s }) => [{
        ...s,
        type: 'editing-gap',
        cursor: origin > cursor ? Math.min(state.value.tokens.length, cursor + 1) : cursor,
        query: ''
      }, []])
      .exhaustive()
    )

    .with({ type: 'navigated', target: 'block-prev' }, () => match<State, [State, Effect[]]>(state.value)
      .with({ type: P.union('editing-gap', 'editing-pill') }, (s) => {
        if (!activeSuggestions.value.length) return [s, []]
        const index = activeSuggestions.value.findIndex(sug => sug.label === s.suggestion)

        // Loop: -1 (input) -> last -> ... -> 0 -> -1 (input)
        const nextIndex = index === -1 ? activeSuggestions.value.length - 1 : index - 1
        return [{ ...s, suggestion: activeSuggestions.value[nextIndex]?.label }, []]
      })
      .otherwise(s => [s, []])
    )

    .with({ type: 'navigated', target: 'block-next' }, () => match<State, [State, Effect[]]>(state.value)
      .with({ type: P.union('editing-gap', 'editing-pill') }, (s) => {
        if (!activeSuggestions.value.length) return [s, []]
        const index = activeSuggestions.value.findIndex(sug => sug.label === s.suggestion)

        // Loop: -1 (input) -> 0 -> ... -> last -> -1 (input)
        const nextIndex = index === activeSuggestions.value.length - 1 ? -1 : index + 1
        return [{ ...s, suggestion: activeSuggestions.value[nextIndex]?.label }, []]
      })
      .otherwise(s => [s, []])
    )

    .with({ type: 'selected-all' }, _ => match<State, [State, Effect[]]>(state.value)
      .with(P.any, ({ tokens, suggestion: _, query: __, ...s }) => [{
        ...s,
        tokens,
        type: 'selecting-pills',
        cursor: tokens.length,
        origin: 0
      }, []])
      .exhaustive()
    )

    .with({ type: 'extended-selection', target: 'inline-prev' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'idle' }, () => [state.value, []])
      .with({ type: 'editing-pill' }, ({ cursor, query: _, suggestion: __, ...s }) => [{
        ...s,
        type: 'selecting-pills',
        cursor: Math.max(0, cursor - 1),
        origin: cursor + 1
      }, []])
      .with({ type: 'editing-gap', cursor: 0 }, () => [state.value, []])
      .with({ type: 'editing-gap' }, ({ cursor, query: _, suggestion: __, ...s }) => [{
        ...s,
        type: 'selecting-pills',
        cursor: cursor - 1,
        origin: cursor
      }, []])
      .with({ type: 'selecting-pills' }, ({ origin, cursor, ...s }) => [{
        ...s,
        ...(cursor === origin + 1 ? ({
          cursor: Math.max(0, origin - 1),
          origin: cursor
        }) : ({
          cursor: Math.max(0, cursor - 1),
          origin
        }))
      }, []])
      .exhaustive()
    )

    .with({ type: 'extended-selection', target: 'inline-next' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'idle' }, () => [state.value, []])
      .with({ type: 'editing-pill' }, ({ tokens, cursor, query: _, suggestion: __, ...s }) => [{
        ...s,
        tokens,
        type: 'selecting-pills',
        cursor: cursor + 1,
        origin: Math.max(0, cursor - 1)
      }, []])
      .with({ type: 'editing-gap', cursor: state.value.tokens.length }, () => [state.value, []])
      .with({ type: 'editing-gap' }, ({ cursor, query: _, suggestion: __, ...s }) => [{
        ...s,
        type: 'selecting-pills',
        cursor: cursor + 1,
        origin: cursor
      }, []])
      .with({ type: 'selecting-pills' }, ({ tokens, origin, cursor, ...s }) => [{
        ...s,
        tokens,
        ...(cursor === origin - 1 ? ({
          cursor: Math.min(tokens.length, origin + 1),
          origin: cursor
        }) : ({
          cursor: Math.min(tokens.length, cursor + 1),
          origin
        }))
      }, []])
      .exhaustive()
    )

    .with({ type: 'typed-query' }, ({ query }) => match<State, [State, Effect[]]>(state.value)
      .with({ query: P.string }, s => [{
        ...s,
        query,
        // TODO: Add 'receivedSuggestions' event to keep suggestion if it still fits
        suggestion: undefined
      }, []])
      .otherwise(s => [s, []])
    )

    .with({ type: 'removed' }, ({ target }) => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'selecting-pills' }, ({ tokens, cursor, origin }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(Math.min(cursor, origin), Math.abs(origin - cursor)),
        type: 'editing-gap',
        cursor: Math.min(cursor, origin),
        query: ''
      }, ['sync-model']])
      .with({ type: 'idle' }, s => [s, []])
      .with({
        cursor: P.when(c => target === 'backward'
          ? c > 0
          : c < state.value.tokens.length
        )
      }, ({ tokens, cursor }) => [{
        redo: undefined,
        undo: state.value,
        type: 'editing-gap',
        ...(target === 'backward' ? ({
          tokens: tokens.toSpliced(cursor - 1, 1),
          cursor: cursor - 1
        }) : ({
          tokens: tokens.toSpliced(cursor, 1),
          cursor
        })),
        query: ''
      }, ['sync-model']])
      .otherwise(s => [s, []])
    )

    .with({ type: 'marked-suggestion' }, ({ suggestion }) => match<State, [State, Effect[]]>(state.value)
      .with({ type: P.union('editing-gap', 'editing-pill')}, s => [{
        ...s,
        suggestion
      }, []])
      .otherwise(s => [s, []])
    )

    .with({ type: 'committed' }, _ => match<State, [State, Effect[]]>(state.value)
      // Delete:
      .with({ type: 'editing-pill' }, s => !s.query.trim() && !s.suggestion, ({ tokens, cursor }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(cursor, 1),
        type: 'editing-gap',
        cursor,
        query: ''
      }, ['sync-model']])
      // Ignore (empty gap):
      .with({ type: 'editing-gap' }, s => !s.query.trim() && !s.suggestion, s => [s, []])
      // Select suggestion:
      .with({ type: P.union('editing-gap', 'editing-pill') }, s => (
        (tokenToCommit => tokenToCommit
          // Valid token resolved -> Commit
          ? [{
              redo: undefined,
              undo: state.value,
              // Insert token when in gap; replace current when in pill:
              tokens: s.tokens.toSpliced(
                s.cursor,
                s.type === 'editing-gap' ? 0 : 1,
                tokenToCommit
              ),
              type: 'selecting-pills',
              cursor: s.cursor + 1,
              origin: s.cursor
            }, ['sync-model']]
          // No token resolved -> soft-block (noop)
          : [s, []]
        )(
          s.suggestion
            // Suggestion was marked explicitly
            ? suggestions.find(suggestion => suggestion.label === s.suggestion)
            // Query matches some suggestion's label
            : suggestions.find(suggestion => suggestion.label.toLowerCase() === s.query.trim().toLowerCase())
              ?? (props.suggestionsOnly
              // Use best match if only suggestions are allowed; fall back to undefined
                ? activeSuggestions.value[0]
              // Create custom token if allowed
                : { label: s.query.trim(), isCustom: true })
        )
      ))
      // Ignore (idle or selecting-pills)
      .otherwise(s => [s, []])
    )

    .with({ type: 'undone' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ undo: P.nonNullable }, ({ undo }) => [{
        ...undo,
        redo: state.value
      }, []])
      .otherwise(s => [s, []])
    )

    .with({ type: 'redone' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ redo: P.nonNullable }, ({ redo }) => [{
        ...redo,
        undo: {
          ...state.value,
          redo: undefined
        }
      }, []])
      .otherwise(s => [s, []])
    )

    .with({ type: 'canceled' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ type: P.union('editing-pill', 'editing-gap'), suggestion: P.string }, s => [{
        ...s,
        suggestion: undefined
      }, []])
      .with({ type: 'editing-pill' }, ({ cursor, suggestion: _, query: __, ...s }) => [{
        ...s,
        type: 'selecting-pills',
        cursor: cursor + 1,
        origin: cursor
      }, []])
      .with({ type: 'editing-gap' }, s => [{
        ...s,
        query: ''
      }, []])
      .with({ type: 'selecting-pills' }, ({ cursor, origin: _, ...s }) => [{
        ...s,
        type: 'editing-gap',
        cursor,
        query: ''
      }, []])
      .with({ type: 'idle' }, s => [s, []])
      .exhaustive()
    )

    .with({ type: 'copied' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'selecting-pills' }, ({ tokens, cursor, origin }) => [state.value, [{
        type: 'write-clipboard',
        tokens: tokens.slice(Math.min(cursor, origin), Math.max(cursor, origin))
      }]])
      .otherwise(s => [s, []])
    )

    .with({ type: 'cut' }, _ => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'selecting-pills' }, ({ tokens, cursor, origin }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(Math.min(cursor, origin), Math.abs(cursor - origin)),
        type: 'editing-gap',
        cursor: Math.min(cursor, origin),
        query: ''
      }, [{
        type: 'write-clipboard',
        tokens: tokens.slice(Math.min(cursor, origin), Math.max(cursor, origin))
      }]])
      .otherwise(s => [s, []])
    )

    .with({ type: 'pasted' }, pasted => match<State, [State, Effect[]]>(state.value)
      .with({ type: 'editing-gap', query: '' }, ({ tokens, cursor }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(cursor, 0, ...pasted.tokens),
        type: 'selecting-pills',
        cursor: cursor + pasted.tokens.length,
        origin: cursor
      }, ['sync-model']])
      .with({ type: 'editing-gap' }, ({ tokens, cursor, query }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(
          cursor,
          0,
          { label: query.trim(), isCustom: true },
          ...pasted.tokens
        ),
        type: 'selecting-pills',
        cursor,
        origin: cursor + 1 + pasted.tokens.length
      }, ['sync-model']])
      .with({ type: 'editing-pill', query: '' }, ({ tokens, cursor }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(
          cursor,
          1,
          ...pasted.tokens
        ),
        type: 'selecting-pills',
        cursor: cursor + pasted.tokens.length,
        origin: cursor
      }, ['sync-model']])
      .with({ type: 'editing-pill' }, ({ tokens, cursor, query }) => [{
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(
          cursor,
          1,
          { ...tokens[cursor], label: query.trim() }, ...pasted.tokens
        ),
        type: 'selecting-pills',
        cursor,
        origin: cursor + 1 + pasted.tokens.length
      }, ['sync-model']])
      .with({ type: 'selecting-pills' }, ({ tokens, cursor, origin, ...s }) => [{
        ...s,
        redo: undefined,
        undo: state.value,
        tokens: tokens.toSpliced(
          Math.min(cursor, origin),
          Math.abs(cursor - origin),
          ...pasted.tokens
        ),
        cursor: Math.min(cursor, origin) + pasted.tokens.length,
        origin: Math.min(cursor, origin)
      }, ['sync-model']])
      .otherwise(s => [s, []])
    )

  // TODO: Implement cross-axis (block) navigation.
  //  .exhaustive()
    .otherwise(_ => [state.value, []])

  // Update state
  state.value = {
    ...newState,
    undo: isEqual(newState.tokens, newState.undo?.tokens)
      // Prune any new undo where the tokens remain the same
      ? newState.undo?.undo
      : newState.undo
  }

  // Trigger side effects
  effects.forEach((effect) => {
    if (effect === 'sync-model') {
      model.value = state.value.tokens
    } else if (effect.type === 'write-clipboard') {
      copy(effect.tokens
        .map(t => t.label)
        .join(',')
      )
    }
  })

  // DEBUG: Record history of all effective events
  dispatchHistory.value.unshift(event)
  dispatchHistory.value.unshift({ ...state.value, undo: `<${state.value.undo?.tokens?.length ?? ''}>`, tokens: `<${state.value.tokens.length}>` })
}

/**
 * Commit first, then dispatch
 * @param event
 */
const commit = (event?: Exclude<Event, { type: 'committed' }>) => {
  dispatch({ type: 'committed' })
  if (event) dispatch(event)
}

// ====================================================================
// Set Focus (Directive)
// ====================================================================

const vFocus: Directive = {
  mounted(el: HTMLInputElement, binding) {
    if (binding.value) {
      el.focus()
      if (binding.modifiers.select) {
        el.select()
      }
    }
  },
  updated(el: HTMLInputElement, binding) {
    if (binding.value && !binding.oldValue) {
      el.focus()
      if (binding.modifiers.select) {
        el.select()
      }
    }
    else if (!binding.value && binding.oldValue) {
      if (binding.modifiers.select) {
        el.setSelectionRange(el.value.length, el.value.length)
      }
    }
  }
}

// ===============
// Set popover-state (Directive)
// ==========

const vPopoverVisible: Directive<HTMLElement, boolean> = {
  mounted(el, binding) {
    if (binding.value && !el.matches(':popover-open')) {
      el.showPopover()
    }
  },
  updated(el, binding) {
    const shouldBeOpen = binding.value
    const isOpen = el.matches(':popover-open')

    if (shouldBeOpen && !isOpen) {
      el.showPopover()
    } else if (!shouldBeOpen && isOpen) {
      el.hidePopover()
    }
  }
}

// ====================================================================
// Helper functions
// ====================================================================

const isAtStart = (el: HTMLInputElement) => el.selectionStart === 0
const isAtEnd = (el: HTMLInputElement) => el.selectionEnd === el.value.length
const isSelected = (el: HTMLInputElement) => isAtStart(el) && isAtEnd(el) && el.selectionStart !== el.selectionEnd

// ====================================================================
// Use logical directional keys (Directive)
// ====================================================================

const vLogicalArrowKeys: Directive<HTMLInputElement> = {
  mounted: el => el.addEventListener('keydown', (e) => {
    const { writingMode, direction } = getComputedStyle(el)

    const action = match([writingMode, direction, e.key])
      // Horizontal inline
      .with(['horizontal-tb', 'ltr', 'ArrowLeft'], () => 'inline-prev')
      .with(['horizontal-tb', 'ltr', 'ArrowRight'], () => 'inline-next')
      .with(['horizontal-tb', 'rtl', 'ArrowLeft'], () => 'inline-next')
      .with(['horizontal-tb', 'rtl', 'ArrowRight'], () => 'inline-prev')

      // Horizontal block
      .with(['horizontal-tb', P._, 'ArrowUp'], () => 'block-prev')
      .with(['horizontal-tb', P._, 'ArrowDown'], () => 'block-next')

      // Vertical inline
      .with([P.string.startsWith('vertical'), P._, 'ArrowUp'], () => 'inline-prev')
      .with([P.string.startsWith('vertical'), P._, 'ArrowDown'], () => 'inline-next')

      // Vertical block
      .with(['vertical-rl', P._, 'ArrowRight'], () => 'block-prev')
      .with(['vertical-rl', P._, 'ArrowLeft'], () => 'block-next')
      .with(['vertical-lr', P._, 'ArrowLeft'], () => 'block-prev')
      .with(['vertical-lr', P._, 'ArrowRight'], () => 'block-next')

      .otherwise(() => null)

    if (!action) return

    el.dispatchEvent(new CustomEvent(`keydown-${action}`, {
      detail: {
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        target: el,
        e,
        atStart: el.selectionStart === 0,
        atEnd: el.selectionEnd === el.value.length
      },
      bubbles: true
    }))
  })
}
</script>

<script lang="ts">
/**
 * Filter and rank items by fuzzy lowercase string similarity
 *
 * @template T - Item type
 * @param query - Search string
 * @param  items - Array of tags to search
 * @param getLabel - Accessor for the string to match against
 * @param config
 * @returns Top n ranked results.
 */
function search<T>(
  query: string,
  items: T[],
  getLabel: (item: T) => string,
  config: {
    /**
     * Maximum number of items to return
     */
    limit: number
    /**
     * Optional filter
     * @default = _ => true
     */
    predicate: (item: T) => boolean
  } = {
    limit: 20,
    predicate: _ => true
  }
): T[] {
  if (!query) return []

  const normalizedQuery = query.toLowerCase()
  const matches: { item: T, score: number }[] = []

  for (const item of items) {
    if (!config.predicate(item)) continue

    const label = getLabel(item).toLowerCase()

    if (label === normalizedQuery) {
      // 1. Exact Match (Highest Priority)
      matches.push({ item, score: 0 })
    } else if (label.startsWith(normalizedQuery)) {
      // 2. Starts With (High Priority)
      matches.push({ item, score: 1 })
    } else if (normalizedQuery.length > 1) {
      // 3. Fuzzy match
      const distance = computeSimpleDistance(normalizedQuery, label)
      if (distance < 3) {
        matches.push({ item, score: 20 + distance })
      }
    }
  }

  return matches
    .sort((a, b) => a.score - b.score)
    .slice(0, config.limit)
    .map(m => m.item)
}

/**
 * Simple distance check (typo tolerance) - upgrade algorithm if necessary
 * @param query
 * @param label
 */
function computeSimpleDistance(query: string, label: string): number {
  let matches = 0
  for (const char of query) {
    if (label.includes(char)) matches++
  }
  return query.length - matches
}

// TODO: Pre-compute a viewModel to reuse some conditions instead of writing them out.
// This adds a layer of indirection but makes the template easier to read - if we get the naming perfect!
// (Balance between directness and DRY)
</script>

<template>
  <Label
    :label
    :required
    :for="`${anchorName}-last-input`"
  >
    <template
      v-if="$slots.label"
      #label
    >
      <slot name="label" />
    </template>
    <div
      v-on-click-outside="() => commit({ type: 'lost-focus' })"
      :class="[
        fw.layout.row,
        fw.shape.field,
        fw.color.default,
        fw.font.caption,
        $style.tokens
      ]"
      :state="state.type"
      ui-block-size="48"
      :ui-sunken="context.uiSunken || undefined"
      @blur="dispatch({ type: 'lost-focus' })"
      @keydown.ctrl.a.prevent="dispatch({ type: 'selected-all' })"
      @keydown.meta.a.prevent="dispatch({ type: 'selected-all' })"
      @keydown.ctrl.z.exact.prevent="dispatch({ type: 'undone' })"
      @keydown.meta.z.exact.prevent="dispatch({ type: 'undone' })"
      @keydown.ctrl.shift.z.exact.prevent="dispatch({ type: 'redone' })"
      @keydown.meta.shift.z.exact.prevent="dispatch({ type: 'redone' })"
      @keydown.ctrl.y.exact.prevent="dispatch({ type: 'redone' })"
      @keydown.meta.y.exact.prevent="dispatch({ type: 'redone' })"
      @paste.prevent="e => e.clipboardData && dispatch({
        type: 'pasted',
        tokens: (e.clipboardData.getData('text/plain') || '')
          .split(',')
          .map(t => t.trim())
          .filter(Boolean)
          .map(label =>
            suggestions.find(s => s.label.toLowerCase() === label.toLowerCase())
            ?? ({ label, isCustom: true })
          )
          .filter(({ isCustom }) =>
            props.suggestionsOnly
              ? !isCustom
              : true
          )
      })"
    >
      <div
        v-for="{ index, token } in [
          { index: 0, token: undefined },
          ...state.tokens.flatMap((token, index) => [
            { index, token },
            { index: index + 1, token: undefined }
          ])
        ]"
        :key="index + String(token)"
        :class="$style.inputSizer"
        size="1"
        :data-value="
          state.type === 'editing-pill' && token && state.cursor === index
            ? `${prefix ?? ''}${state.query}`
            : token
              ? `${prefix ?? ''}${token.label}`
              : state.type === 'editing-gap' && state.cursor === index
                ? `${prefix ?? ''}${state.query}`
                : ''
        "
      >
        <!-- Remove selection -->

        <div :class="$style.pillActions">
          <Button
            v-if="!token && (
              state.type === 'selecting-pills' && index === state.cursor
              || state.type === 'editing-pill' && index === state.cursor + 1
            )"
            v-focus="state.type === 'selecting-pills'"
            v-logical-arrow-keys
            class="color-destructive-ghost shape-circle"
            ui-block-size="24"
            :aria-label="t('vui.remove')"
            :title="t('vui.remove')"
            @focus="commit({
              type: 'activated',
              target: 'gap',
              index,
              extendSelection: true
            })"
            @keydown-inline-prev="dispatch({
              type: $event.detail.shiftKey ? 'extended-selection' : 'navigated',
              target: 'inline-prev'
            })"
            @keydown-inline-next="dispatch({
              type: $event.detail.shiftKey ? 'extended-selection' : 'navigated',
              target: 'inline-next'
            })"
            @click.stop="dispatch({
              // As the button is focused already, the state is guaranteed to be 'selecting-pills'
              type: 'removed',
              target: 'selection'
            })"
            @keyup.delete.prevent.stop="dispatch({
              type: 'removed',
              target: 'selection'
            })"
            @keydown.escape="dispatch({
              type: 'canceled'
            })"
            @keydown.enter.prevent.stop="state.type === 'selecting-pills' && dispatch({
              type: 'activated',
              target: 'pill',
              index: state.origin > index ? index : index - 1,
              extendSelection: false
            })"
            @keydown.ctrl.c.exact.prevent="dispatch({ type: 'copied' })"
            @keydown.meta.c.exact.prevent="dispatch({ type: 'copied' })"
            @keydown.ctrl.x.exact.prevent="dispatch({ type: 'cut' })"
            @keydown.meta.x.exact.prevent="dispatch({ type: 'cut' })"
          >
            <Icon icon="bi:backspace-fill" />
          </Button>
        </div>

        <!-- Gap / Pill content -->

        <input
          :id="/* This is the id that the label component is referring the 'for' to. */
            /* TODO: Fix selector: Only one item can ever have the id */
            'cursor' in state && state.cursor === index
              || (!('cursor' in state) && index === state.tokens.length)
              ? `${anchorName}-last-input`
              : undefined
          "
          v-focus.select="'cursor' in state && state.cursor === index && (
            (token && state.type === 'editing-pill') || (!token && state.type === 'editing-gap')
          )"
          v-logical-arrow-keys
          :tabindex="('cursor' in state && state.cursor === index && ((!token && state.type === 'editing-gap') || (token && state.type === 'editing-pill')) || /* last gap */ !token && state.type === 'idle' && index === state.tokens.length) ? 0 : -1"
          size="1"
          :aria-selected="state.type === 'selecting-pills' && token ? index >= Math.min(state.cursor, state.origin) && index < Math.max(state.cursor, state.origin) : undefined"

          :aria-invalid="(suggestionsOnly && state.query && !suggestions.some(s => s.label.toLowerCase() === (state.query || '').trim().toLowerCase())) || undefined"
          :class="token ? $style.pill : $style.gap"
          :value="
            state.type === 'editing-pill' && token && state.cursor === index
              ? state.query
              : token
                ? `${prefix ?? ''}${token.label}`
                : state.type === 'editing-gap' && state.cursor === index
                  ? state.query
                  : ''
          "

          role="combobox"
          :aria-activedescendant="state.suggestion ? `suggestion-${state.suggestion}` : undefined
          // TODO:
          // - Only add aria-expanded and aria-activedescendant when it's the active or most recent input
          "
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls="suggestions-list"

          @input="dispatch({
            type: 'typed-query',
            query: ($event.target as HTMLInputElement).value
          })"
          @keydown-inline-prev="$event.detail.atStart && commit({
            type: $event.detail.shiftKey ? 'extended-selection' : 'navigated',
            target: 'inline-prev'
          })"
          @keydown-inline-next="$event.detail.atEnd && commit({
            type: $event.detail.shiftKey ? 'extended-selection' : 'navigated',
            target: 'inline-next'
          })"
          @keydown-block-prev="({ detail }: { detail: { e: KeyboardEvent }}) => {
            if (!detail.e.shiftKey) {
              dispatch({
                type: 'navigated',
                target: 'block-prev'
              });
              detail.e.preventDefault();
            }
          }"
          @keydown-block-next="({ detail }: { detail: { e: KeyboardEvent }}) => {
            if (!detail.e.shiftKey) {
              dispatch({
                type: 'navigated',
                target: 'block-next'
              });
              detail.e.preventDefault();
            }
          }"

          @keydown.enter.prevent.stop="token || 'query' in state && state.query ? commit() : dispatch({
            type: 'activated',
            target: 'pill',
            index,
            extendSelection: false
          })"
          @keydown.,.prevent.stop="commit({
            type: 'navigated',
            target: 'inline-next'
          })"
          @keyup.escape="dispatch({
            type: 'canceled'
          })"
          @keydown.backspace.stop="isAtStart($event.target! as HTMLInputElement) && dispatch({
            type: 'removed',
            target: isSelected($event.target! as HTMLInputElement) ? 'forward' : 'backward'
          })"
          @keydown.delete.stop="$event.key === 'Delete' && dispatch({
            type: 'removed',
            target: 'forward'
          })"

          @mousedown.shift.prevent.stop="commit({
            type: 'activated',
            target: token ? 'pill' : 'gap',
            index,
            extendSelection: true
          })"

          v-on="!(state.type === 'editing-pill' && state.cursor === index) ? { 'mousedown.prevent.stop': () => commit({
            type: 'activated',
            target: token ? 'pill' : 'gap',
            index,
            extendSelection: false
          }) } : {}"

          @focus.prevent.stop="commit({
            type: 'activated',
            target: token ? 'pill' : 'gap',
            index,
            extendSelection: false
          })"
        >
      </div>
      <!-- UNDO and REDO buttons -->

      <span :class="$style.toolbar">

        <span v-if="state.type !== 'idle' && (state.undo || state.redo)">
          <Button
            :disabled="!state.undo"
            class="shape-square"
            ui-block-size="24"
            :aria-label="t('vui.undo')"
            :title="t('vui.undo')"
            @mousedown.prevent
            @click="dispatch({ type: 'undone' })"
          >
            <Icon icon="boxicons:undo-stroke-alt" />
          </Button>
          <Button
            :disabled="!state.redo"
            class="shape-square"
            ui-block-size="24"
            :aria-label="t('vui.redo')"
            :title="t('vui.redo')"
            @mousedown.prevent
            @click="dispatch({ type: 'redone' })"
          >
            <Icon icon="boxicons:redo-stroke-alt" />
          </Button>
        </span>
      </span>
      <!-- Select suggestions -->

      <menu
        id="suggestions-list"
        v-popover-visible="activeSuggestions.length > 0"
        popover="manual"
        role="listbox"
        class="color-default shape-field"
        :class="$style.dropdown"
        :style="`position-anchor: ${anchorName}`"
      >
        <li
          v-for="{ label } in activeSuggestions"
          :id="`suggestion-${label}`"
          :key="label"
          role="option"
          :aria-selected="state.suggestion === label ? 'true' : undefined"
          @mousedown.prevent="dispatch({ type: 'marked-suggestion', suggestion: label })"
          @click="commit()"
        >
          {{ prefix ?? '' }}{{ label }}
        </li>
      </menu>
    </div>
    <div
      v-for="(e, index) in dispatchHistory"
      :key="index"
      :class="$style.debug"
    >
      {{ e }}
      <br>
    </div>
  </Label>
</template>

<style module>
.tokens {
    background: var(--color-background);
    color: var(--color);
    padding: 0 0;
    position: relative;

    &[ui-sunken] {
        background: var(--color-150);
    }

    &:hover {
      outline: 3px var(--color-350);
      outline-offset: -3px;
    }
}

.pillActions {
    position: absolute;
    z-index: 1;
    inset: -32px 0 auto auto;
}

/* Responsive Input (Firefox compatible) */

.inputSizer {
  display: inline-grid;
  position: relative;

  &:has(>.gap) {
      padding: 0 8px;
      margin: 0 -8px;
  }
}
.inputSizer::after, .inputSizer>input {
  grid-area: 1 / 1;
  padding: inherit;
  font: inherit;
}
.inputSizer::after {
  content: attr(data-value) "\200B";
  visibility: hidden;
  white-space: pre;
  padding: inherit;
}
.inputSizer > input {
    /* FIREFOX FIX: */
    /* Minimum inline-size of an input */
    inline-size: 0;
    /* Stretch to fill grid cell */
    min-inline-size: 100%;
}

input[aria-invalid="true"] {
  text-decoration: line-through;
}

input.gap {
    background: transparent;
    border: none;
    z-index: 1;
    color: var(--color-foreground);

    &:focus:not([aria-invalid]) {
        outline: none;
    }

    &:focus[aria-invalid="true"] {
        opacity: 0.8;
        outline-color: transparent;
    }

    /*&:focus[value=""] {
        outline: 4px solid var(--color-background-editing);
        outline-offset: -8px;
        animation: flash .3s steps(1, end) 2 reverse
    }*/
}

input.pill {
    position: relative;
    background: transparent;
    border: none;
    /* Minimal inline-size for very short pills */
    inline-size: 24px;
    text-align: center;
}

input:focus {
  anchor-name: v-bind(anchorName);
}

/* Extend the last gap if tokens control is wider than its contained pills */
:has(>.gap):last-of-type {
    flex-grow: 1;
}

:has(>.pill) {
    position: relative;

      /* Visual pill shape (encroaches into gaps) */
      &::before {
        content: '';
        position: absolute;
        inset: 0 -10px;
        border-radius: 999px;
        z-index: 0;
      }

      /* Input field */
      > .pill {
          color: var(--color-foreground);
      }
}

/* Add visual cutout (Default LTR: cutout on the left) */
.tokens:where([state="idle"], [state="selecting-pills"]) :has(>.pill)~:has(>.pill:not([aria-selected="true"]))::before {
    mask: radial-gradient(15px circle at -8px center, #0000 99.5%, #000 100%);
}

/* RTL override (cutout on the right) */
.tokens:where([state="idle"], [state="selecting-pills"]):dir(rtl) :has(>.pill)~:has(>.pill:not([aria-selected="true"]))::before {
    mask: radial-gradient(15px circle at calc(100% + 8px) center, #0000 99.5%, #000 100%);
}

:has(>.pill[aria-selected=true]) {
    &::before {
        background-color: var(--color-background-selected);
    }
    > .pill {
        color: var(--color-foreground-selected);
    }
}

:has(>.pill:focus) {
    ::selection {
        background: var(--color-background-editing);
        color: var(--color-foreground-editing)
    }
    &::before {
        outline: 2px var(--color-focus);
    }
    > .pill {
        outline: none;
        color: var(--color-foreground);
    }

    &::before {
        background-color: var(--color-150);
    }
}

:has(>.pill) {
  &::before {
      background-color: var(--color-250);
  }
}

[state="idle"] :has(>.pill) {
    &::before {
        background-color: var(--color-250);
    }
    > .pill {
        color: var(--color-foreground);
    }
    &:has(>.pill:hover)::before {
        background-color: var(--color-300);
    }
}

:is(.pill, .gap):not(:focus):hover {
    /* TODO: Add visible hover indicator for editable gaps and pills */
}

.dropdown {
    position: fixed;
    z-index: 1;
    box-shadow: 0px 2px 8px 0px var(--color-shadow);

    /* Reset native popover defaults */
    border: none;
    margin: 0;
    padding: 0;
    overflow: visible;

    position-area: block-end span-inline-end;
    position-try-fallbacks: flip-block, flip-inline, flip-block flip-inline;

    background: var(--color-200);
    width: max-content;
    padding: 0;
    margin: 0;

    /* Sever inherited flexbox cross-axis alignment */
        align-self: start !important;

        /* If the row is also applying inline/main-axis alignment (justify-content: center),
           sever that too just to be safe: */
        justify-self: start !important;

    > li {
        list-style-type: none;
        padding: 0 8px;
        min-width: 64px;
        &::before{
            content: '';
        }

        &:first-child {
            padding-block-start: 8px;
            border-start-start-radius: 8px;
            border-start-end-radius: 8px;
        }

        &:last-child {
            padding-block-end: 8px;
            border-end-start-radius: 8px;
            border-end-end-radius: 8px;
        }
    }

    > li:hover {
        background: var(--color-background-hover);
        color: var(--color-foreground-hover)
    }

    > li[aria-selected] {
        background: var(--color-background-selected);
        color: var(--color-foreground-selected)
    }
}

.toolbar {
    position: absolute;
    inset-block-end: -32px;
    inset-inline-end: 0;
}

/* Confirmation animation */

@keyframes flash {
  50% {
    opacity: .5;
  }
}

/* DEBUG */
.debug {
    display:none;
}
.debug:nth-child(odd){
    background-color:brown;
    color: white;
}
</style>
